// src/pages/admin/SubscriptionsPage.jsx
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Plus } from 'lucide-react'
import { adminSubscriptionsApi } from '../../api/services'
import { Card, CardHeader, CardBody, Button, StatusBadge, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

export default function SubscriptionsPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('plans')
  const [editPlan, setEditPlan] = useState(null)
  const [form, setForm] = useState({})

  const { data, isLoading } = useQuery({ queryKey: ['admin-subscriptions', tab], queryFn: () => tab === 'plans' ? adminSubscriptionsApi.plans() : adminSubscriptionsApi.subscribers() })
  const savePlan = useMutation({ mutationFn: () => adminSubscriptionsApi.savePlan(form, editPlan !== 'new' ? editPlan : null), onSuccess: () => { toast.success('Plan saved'); qc.invalidateQueries(['admin-subscriptions']); setEditPlan(null) }, onError: e => toast.error(e.response?.data?.message||'Error') })

  if (isLoading) return <PageSpinner />
  const payload = data?.data?.data || {}

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-display font-bold">Subscriptions</h1>
        {tab === 'plans' && <Button size="sm" onClick={() => { setEditPlan('new'); setForm({ name:'', slug:'', monthly_price:0, annual_price:0, commission_rate:10 }) }}><Plus size={14} /> New Plan</Button>}
      </div>
      <div className="flex gap-2 mb-6">
        {['plans','subscribers'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all capitalize ${tab === t ? 'bg-pink text-white border-pink' : 'border-border text-muted hover:border-pink'}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'plans' && (
        <>
          {editPlan && (
            <Card className="mb-6">
              <CardHeader><span className="font-bold text-sm">{editPlan === 'new' ? 'New Plan' : 'Edit Plan'}</span></CardHeader>
              <CardBody className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className="input" placeholder="Name *" value={form.name||''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  <input className="input" placeholder="Slug *" value={form.slug||''} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} />
                  <input className="input" type="number" placeholder="Monthly price" value={form.monthly_price||''} onChange={e => setForm(f => ({ ...f, monthly_price: e.target.value }))} />
                  <input className="input" type="number" placeholder="Annual price" value={form.annual_price||''} onChange={e => setForm(f => ({ ...f, annual_price: e.target.value }))} />
                  <input className="input" type="number" placeholder="Product limit" value={form.product_limit||''} onChange={e => setForm(f => ({ ...f, product_limit: e.target.value }))} />
                  <input className="input" type="number" placeholder="Commission rate %" value={form.commission_rate||''} onChange={e => setForm(f => ({ ...f, commission_rate: e.target.value }))} />
                </div>
                <div className="flex gap-4 text-sm flex-wrap">
                  {['analytics_enabled','storefront_enabled','rfq_enabled','featured_listing'].map(k => (
                    <label key={k} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={!!form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} />
                      {k.replace(/_/g,' ')}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button loading={savePlan.isPending} onClick={() => savePlan.mutate()} disabled={!form.name || !form.slug}>Save</Button>
                  <Button variant="ghost" onClick={() => setEditPlan(null)}>Cancel</Button>
                </div>
              </CardBody>
            </Card>
          )}
          <div className="grid sm:grid-cols-3 gap-4">
            {(payload.plans || []).map(p => (
              <Card key={p.id}>
                <CardBody>
                  <p className="font-bold">{p.name}</p>
                  <p className="text-pink font-bold mt-1">₹{Number(p.monthly_price).toLocaleString('en-IN')}/mo</p>
                  <p className="text-xs text-muted mt-0.5">{p.active_count} active subscribers</p>
                  <Button size="sm" variant="secondary" className="mt-3" onClick={() => { setEditPlan(p.id); setForm(p) }}>Edit</Button>
                </CardBody>
              </Card>
            ))}
          </div>
        </>
      )}

      {tab === 'subscribers' && (
        <div className="space-y-2">
          {(payload.data || []).map(s => (
            <Card key={s.id}>
              <CardBody className="flex items-center justify-between text-sm">
                <div>
                  <p className="font-bold">{s.supplier?.business_name || s.supplier?.name}</p>
                  <p className="text-xs text-muted">{s.plan?.name} · {s.billing_cycle}</p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={s.status} />
                  <span className="text-xs text-muted">{s.expires_at ? new Date(s.expires_at).toLocaleDateString('en-IN') : '—'}</span>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
